'use client';

import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { useImageLoaded } from '@/lib/image-cache';

// =============================================================================
// Level Definitions
// =============================================================================

export const LEVEL_LABELS: Record<number, string> = {
  0: 'Lurker',
  1: 'Locked In',
  2: 'Verified',
  3: 'Recruiter',
  4: 'Kingmaker',
  5: 'Legend',
  6: 'Founding OG',
};

export const LEVEL_COLORS: Record<number, string> = {
  0: 'text-muted-foreground',
  1: 'text-blue-400',
  2: 'text-purple-400',
  3: 'text-yellow-400',
  4: 'text-amber-300',
  5: 'text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400',
  6: 'text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-yellow-400 to-purple-400',
};

/**
 * Human-readable description of what each level's avatar flair looks like.
 */
export const LEVEL_FLAIR_DESCRIPTIONS: Record<number, string> = {
  0: 'Gray border — no flair',
  1: 'Solid blue border',
  2: 'Animated blue-purple gradient border',
  3: 'Thick gold border',
  4: 'Animated gold shimmer border',
  5: 'Animated rainbow gradient border',
  6: 'Animated rainbow border + glowing aura',
};

// =============================================================================
// Component
// =============================================================================

export interface AvatarWithFlairProps {
  avatarUrl?: string | null;
  address?: string | null;
  level: number;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Prefer loading the social avatar sooner (header / portfolio trigger). */
  priority?: boolean;
  /**
   * Fires once the displayed avatar is ready to paint (image loaded, failed,
   * or no URL). R3F `frameloop="demand"` canvases should use this to
   * `invalidate()` so the texture becomes visible without user input.
   */
  onDisplayReady?: () => void;
  /**
   * Tighten the flair ring so the avatar image takes proportionally more
   * of the component's footprint. Used by the 3D board's avatar overlay
   * on small screens / large grids where the default ring thickness
   * eats a noticeable chunk of a 30-pixel-wide tile.
   *
   * Compare:
   *   normal: `outer = inner + ~8px` (4px ring on each side)
   *   compact: `outer = inner + ~4px` (2px ring on each side)
   *
   * Flair identity (gradient, shimmer, rainbow, glow) is preserved —
   * only the ring thickness changes.
   *
   * Ignored when `borderPx` is set — explicit numeric sizing wins.
   */
  compact?: boolean;
  /**
   * Override the ring thickness with an explicit pixel value. Skips the
   * `size` × `compact` bucket map and the `borderWeight` table; the
   * caller computes the value (e.g. from a per-tile projected pixel
   * size) and the ring scales linearly between 1px and 8px.
   *
   * Used by the lobby canvas tiles to keep the flair proportional to
   * the avatar's on-screen size as the user pans / zooms — without
   * this the ring is a fixed CSS pixel value (1px when far away, 4px
   * when close in), which reads as overly thin at zoom in / overly
   * thick at zoom out. Other surfaces (profile modals, leaderboards,
   * portfolio cards) keep the bucketed sizing.
   */
  borderPx?: number;
}

/**
 * Outer = flair ring; inner = avatar crop — inner is inset smaller than outer so
 * level borders / gradient rings read thick and engaging (matches `develop`).
 *
 * `xs`/`sm` defaults match the pre-c442b06 ring weight so desktop
 * surfaces (leaderboard rows, header clusters, chat avatars, drawer
 * portraits, holders list, etc.) keep the prominent flair border that
 * identifies level / referral tier at a glance. Callers that genuinely
 * need a thinner ring — primarily the 3D tile-overlay on dense grids
 * or narrow viewports — opt in via the `compact` prop, which switches
 * to `COMPACT_INNER_OVERRIDE` below. The mobile redesign briefly made
 * the thin ring the global default for these sizes, which made every
 * desktop list look unfinished.
 */
const SIZE_MAP = {
  xxs: { outer: 'w-5 h-5', inner: 'w-[16px] h-[16px]', outerRadius: 'rounded-[3px]', innerRadius: 'rounded-[2px]' },
  xs: { outer: 'w-6 h-6', inner: 'w-[20px] h-[20px]', outerRadius: 'rounded-[4px]', innerRadius: 'rounded-[3px]' },
  sm: { outer: 'w-10 h-10', inner: 'w-[2rem] h-[2rem]', outerRadius: 'rounded-md', innerRadius: 'rounded-[4px]' },
  md: { outer: 'w-14 h-14', inner: 'w-[2.75rem] h-[2.75rem]', outerRadius: 'rounded-lg', innerRadius: 'rounded-md' },
  lg: { outer: 'w-20 h-20', inner: 'w-16 h-16', outerRadius: 'rounded-lg', innerRadius: 'rounded-md' },
  xl: { outer: 'w-28 h-28', inner: 'w-[5.5rem] h-[5.5rem]', outerRadius: 'rounded-lg', innerRadius: 'rounded-md' },
};

/**
 * Optional override map used when the caller sets `compact`. Every
 * entry shrinks the ring by another ~2px on each side (total ~4px
 * smaller ring) vs the default `SIZE_MAP` tuning. Used by the 3D
 * board's avatar overlay on small viewports / large grids where even
 * the default ring still eats noticeable avatar pixels.
 *
 * We keep the outer (footprint) size fixed so callers' layouts don't
 * jump when `compact` toggles — only the inner crop grows.
 */
const COMPACT_INNER_OVERRIDE: Record<keyof typeof SIZE_MAP, string> = {
  xxs: 'w-[18px] h-[18px]',
  xs: 'w-[23px] h-[23px]',
  sm: 'w-[38px] h-[38px]',
  md: 'w-[52px] h-[52px]',
  lg: 'w-[76px] h-[76px]',
  xl: 'w-[106px] h-[106px]',
};

/**
 * Avatar with level-dependent border flair.
 *
 * Level 0: gray/muted border (boring)
 * Level 1: solid blue border
 * Level 2: animated gradient border (blue-purple)
 * Level 3: gold border (5+ referrals)
 * Level 4: animated gold shimmer (20+ referrals)
 * Level 5: animated rainbow gradient (50+ referrals)
 * Level 6: animated rainbow + glow (100+ referrals)
 */
export function AvatarWithFlair({
  avatarUrl,
  address,
  level,
  size = 'md',
  className,
  priority = false,
  onDisplayReady,
  compact = false,
  borderPx,
}: AvatarWithFlairProps) {
  const baseConfig = SIZE_MAP[size];
  // `compact` shrinks the inner crop. Explicit `borderPx` doesn't —
  // the inner crop on the canvas tile is sized by its own wrapper
  // (the 60% / 70% percent-size on `TileFlairAvatar` — see
  // `LobbySeatTile2D.tsx`), and the AvatarWithFlair inner classes
  // would just collide with that. So `borderPx` callers get the
  // base (non-compact) inner sizing.
  const sizeConfig =
    compact && borderPx === undefined
      ? { ...baseConfig, inner: COMPACT_INNER_OVERRIDE[size] }
      : baseConfig;

  // Image loading — social avatar with blocky fallback
  const socialAvatarSrc = avatarUrl || undefined;
  const fallbackAvatarSrc = address
    ? generateGridAvatarUrl(address, 128)
    : generateGridAvatarUrl('0x0000000000000000000000000000000000000000', 128);
  const { loaded: imgLoaded, onLoad: onImgLoad, onError: onImgError, failed: imgFailed } = useImageLoaded(socialAvatarSrc);
  const showSrc = (socialAvatarSrc && !imgFailed) ? socialAvatarSrc : fallbackAvatarSrc;
  const isReady = imgFailed || !socialAvatarSrc || imgLoaded;

  useEffect(() => {
    if (isReady) onDisplayReady?.();
  }, [isReady, onDisplayReady]);

  // Determine outer border styles based on level (thinner for xs/sm, and
  // compact-tuned variants step down one bucket further so md/lg/xl's
  // padded rings shrink to ~2px instead of 4-6px).
  const borderWeight = (() => {
    if (compact) {
      if (size === 'xxs' || size === 'xs') return 'extra-thin' as const;
      if (size === 'sm' || size === 'md') return 'thin' as const;
      return 'normal' as const;
    }
    if (size === 'xxs') return 'extra-thin' as const;
    if (size === 'xs' || size === 'sm') return 'thin' as const;
    return 'normal' as const;
  })();
  // Numeric `borderPx` overrides the bucketed weight (see prop
  // doc-comment) — emits inline-style sizing so the ring scales
  // smoothly with the caller-provided value.
  const flairOuter =
    borderPx !== undefined
      ? getFlairOuterStyle(level, borderPx)
      : null;
  const outerClasses = flairOuter ? flairOuter.className : getOuterClasses(level, borderWeight);
  const outerStyle = flairOuter ? flairOuter.style : undefined;

  const glowClass = level >= 6
    ? (size === 'xxs' || size === 'xs' || size === 'sm') ? 'flair-glow-sm' : 'flair-glow'
    : undefined;

  return (
    <div
      className={cn(
        'relative flex items-center justify-center shrink-0',
        sizeConfig.outer,
        sizeConfig.outerRadius,
        outerClasses,
        glowClass,
        className
      )}
      style={outerStyle}
    >
      <div
        className={cn(
          'relative overflow-hidden bg-background',
          sizeConfig.inner,
          sizeConfig.innerRadius,
        )}
      >
        {/* Shimmer placeholder while social avatar loads */}
        {!isReady && (
          <div className={cn("absolute inset-0 bg-secondary animate-pulse", sizeConfig.innerRadius)} />
        )}
        {/* Hidden img to detect load/error of social avatar.
         *  Must stay `loading="eager"` — the 1×1 `sr-only` clip makes
         *  lazy-loading unreliable inside Radix Dialog portals (profile
         *  modal), where IntersectionObserver never fires and the shimmer
         *  stays stuck forever. The displayed `<img>` below still uses the
         *  caller's preferred lazy/eager hint for the actual viewport paint. */}
        {socialAvatarSrc && !imgFailed && !imgLoaded && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={socialAvatarSrc}
            alt=""
            className="sr-only"
            decoding="async"
            fetchPriority={priority ? 'high' : 'low'}
            loading="eager"
            onLoad={onImgLoad}
            onError={onImgError}
          />
        )}
        {/* Displayed avatar — fades in once ready */}
        <img
          src={showSrc}
          alt=""
          decoding="async"
          fetchPriority={priority ? 'high' : 'low'}
          loading={priority ? 'eager' : 'lazy'}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isReady ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
    </div>
  );
}

/**
 * Level-aware classes for the outer ring of a flair avatar. Exported so
 * renderers that can't use the full `AvatarWithFlair` component (e.g. the
 * Lite-mode 2D tile, which needs a percentage-sized avatar that scales with
 * the board) can still match the same visual border treatment — pass `normal`
 * for thick rings aligned with the 3D HUD.
 */
export function getFlairOuterClasses(
  level: number,
  borderWeight: 'extra-thin' | 'thin' | 'normal' = 'normal',
): string {
  return getOuterClasses(level, borderWeight);
}

/**
 * Matching glow class for the same level/size bucket. Level ≥ 6 pulses a
 * coloured aura; everything else returns `undefined`. Exported alongside
 * `getFlairOuterClasses` so external renderers stay visually consistent.
 */
export function getFlairGlowClass(
  level: number,
  size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl',
): string | undefined {
  if (level < 6) return undefined;
  return size === 'xxs' || size === 'xs' || size === 'sm'
    ? 'flair-glow-sm'
    : 'flair-glow';
}

/**
 * Inline-style variant of {@link getFlairOuterClasses} for callers that
 * need to size the border ring against a per-frame measurement (e.g.
 * the lobby canvas, where each tile's projected pixel size is known).
 *
 * Returns:
 *   • `className` — the level-specific colour / gradient class
 *     (`flair-gradient-border`, `flair-rainbow`, `border-blue-500`, …).
 *     Pass through to the wrapper element.
 *   • `style` — `borderWidth` (for solid-colour levels) or `padding`
 *     (for gradient/shimmer/rainbow levels), expressed in px. Caller
 *     merges into the wrapper's inline style.
 *
 * `borderPx` is clamped to `[1, 8]` — matches the visual range covered
 * by the existing class-based buckets (`extra-thin` 1px → `normal`
 * 4–6px) and prevents pathological values from rendering a ring that
 * eats the entire avatar.
 *
 * Solid-colour levels (0/1/3) need `border: <px> solid <colour>` to
 * paint, so we emit `borderWidth` *and* `borderStyle: 'solid'`. Tailwind's
 * `border-blue-500` already sets the colour; without `borderStyle` the
 * colour class wouldn't render any visible stroke. Padded levels (2/4/5/6)
 * use the existing keyframe-animated background classes; only the inner
 * inset padding scales.
 */
export function getFlairOuterStyle(
  level: number,
  borderPx: number,
): { className: string; style: React.CSSProperties } {
  const px = Math.max(1, Math.min(8, Math.round(borderPx)));
  switch (level) {
    case 1:
      return {
        className: 'border-blue-500',
        style: { borderWidth: px, borderStyle: 'solid' },
      };
    case 2:
      return {
        className: 'flair-gradient-border',
        style: { padding: px },
      };
    case 3:
      return {
        className: 'border-yellow-500',
        style: { borderWidth: px, borderStyle: 'solid' },
      };
    case 4:
      return {
        className: 'flair-gold-shimmer',
        style: { padding: px },
      };
    case 5:
      return {
        className: 'flair-rainbow',
        style: { padding: px },
      };
    case 6:
      // Level 6 normally bumps padding +2 over the base for the
      // glow-anchored rainbow band — preserve that proportion when
      // sized inline.
      return {
        className: 'flair-rainbow',
        style: { padding: Math.min(8, px + 2) },
      };
    case 0:
    default:
      return {
        className: 'border-border/40',
        style: { borderWidth: px, borderStyle: 'solid' },
      };
  }
}

function getOuterClasses(level: number, borderWeight: 'extra-thin' | 'thin' | 'normal' = 'normal'): string {
  if (borderWeight === 'extra-thin') {
    switch (level) {
      case 0:
        return 'border border-border/40';
      case 1:
        return 'border border-blue-500';
      case 2:
        return 'flair-gradient-border p-[1px]';
      case 3:
        return 'border border-yellow-500';
      case 4:
        return 'flair-gold-shimmer p-[1px]';
      case 5:
        return 'flair-rainbow p-[1px]';
      case 6:
        return 'flair-rainbow p-[1px]';
      default:
        return 'border border-border/40';
    }
  }
  if (borderWeight === 'thin') {
    switch (level) {
      case 0:
        return 'border-[1.5px] border-border/40';
      case 1:
        return 'border-[1.5px] border-blue-500';
      case 2:
        return 'flair-gradient-border p-[1.5px]';
      case 3:
        return 'border-[1.5px] border-yellow-500';
      case 4:
        return 'flair-gold-shimmer p-[1.5px]';
      case 5:
        return 'flair-rainbow p-[1.5px]';
      case 6:
        return 'flair-rainbow p-[1.5px]';
      default:
        return 'border-[1.5px] border-border/40';
    }
  }
  switch (level) {
    case 0:
      return 'border-[4px] border-border/40';
    case 1:
      return 'border-[4px] border-blue-500';
    case 2:
      return 'flair-gradient-border p-[4px]';
    case 3:
      return 'border-[4px] border-yellow-500';
    case 4:
      return 'flair-gold-shimmer p-[5px]';
    case 5:
      return 'flair-rainbow p-[5px]';
    case 6:
      return 'flair-rainbow p-[6px]';
    default:
      return 'border-[4px] border-border/40';
  }
}

/**
 * Level badge component — shows the level label with color.
 */
export function LevelBadge({
  level,
  className,
}: {
  level: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'text-xs font-semibold',
        LEVEL_COLORS[level] || LEVEL_COLORS[0],
        className
      )}
    >
      Lvl {level} {LEVEL_LABELS[level] || ''}
    </span>
  );
}
